import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { MessageSquare, Mail, Phone, Mic } from 'lucide-react';
import type { ChannelStats, EmailChannelStats, VoiceChannelStats } from '@respectly/shared-types';

interface ProgressBarProps {
  value: number;
  max: number;
  color: string;
  label: string;
  count: number;
  pct?: number;
}

function ProgressBar({ value, max, color, label, count, pct }: ProgressBarProps) {
  const width = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">{label}</span>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-bold tabular-nums text-slate-700 dark:text-slate-300">
            {count.toLocaleString()}
          </span>
          {pct !== undefined && (
            <span className="text-[10px] text-slate-400 dark:text-slate-500">({pct}%)</span>
          )}
        </div>
      </div>
      <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${width}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

interface SmsCardProps {
  data: ChannelStats;
  isLoading?: boolean;
}

export function SmsChannelCard({ data, isLoading }: SmsCardProps) {
  if (isLoading) return <ChannelCardSkeleton />;

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 flex-1">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center">
              <MessageSquare className="h-4 w-4 text-blue-500" />
            </div>
            <div>
              <CardTitle className="text-sm font-semibold text-slate-900 dark:text-slate-100">SMS</CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400">{data.sent.toLocaleString()} sent</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold tabular-nums text-emerald-600 dark:text-emerald-400">{data.deliveryRate}%</div>
            <div className="text-[10px] text-slate-400">delivery rate</div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {data.sent === 0 ? (
          <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-4">No SMS sent this period</p>
        ) : (
          <>
            <ProgressBar value={data.sent} max={data.sent} color="#3b82f6" label="Sent" count={data.sent} />
            <ProgressBar value={data.delivered} max={data.sent} color="#10b981" label="Delivered" count={data.delivered} pct={data.deliveryRate} />
            <ProgressBar value={data.failed} max={data.sent} color="#ef4444" label="Failed" count={data.failed} pct={data.failureRate} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface EmailCardProps {
  data: EmailChannelStats;
  isLoading?: boolean;
}

export function EmailChannelCard({ data, isLoading }: EmailCardProps) {
  if (isLoading) return <ChannelCardSkeleton />;

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 flex-1">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-violet-500/10 dark:bg-violet-500/20 flex items-center justify-center">
              <Mail className="h-4 w-4 text-violet-500" />
            </div>
            <div>
              <CardTitle className="text-sm font-semibold text-slate-900 dark:text-slate-100">Email</CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400">{data.sent.toLocaleString()} sent</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold tabular-nums text-emerald-600 dark:text-emerald-400">{data.openRate}%</div>
            <div className="text-[10px] text-slate-400">open rate</div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {data.sent === 0 ? (
          <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-4">No emails sent this period</p>
        ) : (
          <>
            <ProgressBar value={data.sent} max={data.sent} color="#8b5cf6" label="Sent" count={data.sent} />
            <ProgressBar value={data.delivered} max={data.sent} color="#10b981" label="Delivered" count={data.delivered} pct={data.deliveryRate} />
            <ProgressBar value={data.opened} max={data.delivered || data.sent} color="#3b82f6" label="Opened" count={data.opened} pct={data.openRate} />
            <ProgressBar value={data.clicked} max={data.opened || data.sent} color="#f59e0b" label="Clicked" count={data.clicked} pct={data.clickRate} />
            <ProgressBar value={data.failed} max={data.sent} color="#ef4444" label="Failed" count={data.failed} pct={data.failureRate} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface VoiceCardProps {
  data: VoiceChannelStats;
  isLoading?: boolean;
}

export function VoiceChannelCard({ data, isLoading }: VoiceCardProps) {
  if (isLoading) return <ChannelCardSkeleton />;

  const avgMin = Math.floor(data.avgDurationSeconds / 60);
  const avgSec = data.avgDurationSeconds % 60;
  const avgLabel = data.avgDurationSeconds > 0 ? `${avgMin}:${String(avgSec).padStart(2, '0')}` : '—';

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 flex-1">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-amber-500/10 dark:bg-amber-500/20 flex items-center justify-center">
              <Phone className="h-4 w-4 text-amber-500" />
            </div>
            <div>
              <CardTitle className="text-sm font-semibold text-slate-900 dark:text-slate-100">Voice</CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400">{data.initiated.toLocaleString()} initiated</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold tabular-nums text-amber-600 dark:text-amber-400">{data.completionRate}%</div>
            <div className="text-[10px] text-slate-400">completion</div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {data.initiated === 0 ? (
          <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-4">No voice calls this period</p>
        ) : (
          <>
            <ProgressBar value={data.initiated} max={data.initiated} color="#f59e0b" label="Initiated" count={data.initiated} />
            <ProgressBar value={data.completed} max={data.initiated} color="#10b981" label="Completed" count={data.completed} pct={data.completionRate} />
            <ProgressBar value={data.consentCaptured} max={data.completed || data.initiated} color="#8b5cf6" label="Consent Captured" count={data.consentCaptured} pct={data.consentCaptureRate} />
            <ProgressBar value={data.failed} max={data.initiated} color="#ef4444" label="Failed" count={data.failed} />

            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2">
              <Mic className="h-3 w-3 text-slate-400" />
              <span className="text-[11px] text-slate-500 dark:text-slate-400">Avg duration:</span>
              <span className="text-[11px] font-bold tabular-nums text-slate-700 dark:text-slate-300">{avgLabel}</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ChannelCardSkeleton() {
  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 flex-1">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
          <div className="space-y-1">
            <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
            <div className="h-2 w-12 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-1">
            <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
